import { useRef, useCallback } from 'react';
import * as THREE from 'three';

interface LipSyncData {
  viseme: string;
  intensity: number;
}

export const useLipSync = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Mapeo de frecuencias a visemas de Ready Player Me
  const visemeMapping = {
    viseme_sil: { freq: [0, 200], intensity: 0 },      // Silencio
    viseme_PP: { freq: [200, 400], intensity: 0.3 },   // P, B, M
    viseme_FF: { freq: [400, 800], intensity: 0.25 },  // F, V
    viseme_TH: { freq: [800, 1200], intensity: 0.2 },  // TH
    viseme_DD: { freq: [1200, 1600], intensity: 0.3 }, // D, T, N, L
    viseme_kk: { freq: [1600, 2000], intensity: 0.25 }, // K, G
    viseme_CH: { freq: [2000, 2400], intensity: 0.2 }, // CH, SH, J
    viseme_SS: { freq: [2400, 3200], intensity: 0.15 }, // S, Z
    viseme_nn: { freq: [3200, 4000], intensity: 0.15 }, // N, NG
    viseme_RR: { freq: [4000, 5000], intensity: 0.2 }, // R
    viseme_aa: { freq: [5000, 6000], intensity: 0.4 }, // A
    viseme_E: { freq: [6000, 7000], intensity: 0.3 },  // E
    viseme_I: { freq: [7000, 8000], intensity: 0.25 }, // I
    viseme_O: { freq: [8000, 9000], intensity: 0.35 }, // O
    viseme_U: { freq: [9000, 10000], intensity: 0.2 }  // U
  };

  const applyLipSync = useCallback((model: THREE.Group, lipSyncData: LipSyncData[]) => {
    if (!model) return;

    model.traverse((child: THREE.Object3D) => {
      if (
        child instanceof THREE.SkinnedMesh &&
        child.morphTargetDictionary &&
        child.morphTargetInfluences
      ) {
        // Reset all visemes
        Object.keys(visemeMapping).forEach((viseme) => {
          const index = child.morphTargetDictionary![viseme];
          if (index !== undefined && child.morphTargetInfluences) {
            child.morphTargetInfluences[index] = 0;
          }
        });

        // Apply current visemes
        lipSyncData.forEach(({ viseme, intensity }) => {
          const index = child.morphTargetDictionary![viseme];
          if (index !== undefined && child.morphTargetInfluences) {
            child.morphTargetInfluences[index] = Math.min(intensity * 0.6, 0.4); // Reducir aún más
          }
        });

        // Apply mouth open based on overall volume (muy reducido)
        const overallIntensity = lipSyncData.reduce((sum, data) => sum + data.intensity, 0) / lipSyncData.length;
        const mouthOpenIndex = child.morphTargetDictionary!['mouthOpen'];
        if (mouthOpenIndex !== undefined && child.morphTargetInfluences) {
          child.morphTargetInfluences[mouthOpenIndex] = Math.min(overallIntensity * 0.1, 0.15);
        }
      }
    });
  }, []);

  const analyzeAudio = useCallback((audioElement: HTMLAudioElement, model: THREE.Group) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;
    const source = audioContext.createMediaElementSource(audioElement);
    const analyser = audioContext.createAnalyser();
    
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    source.connect(analyser);
    analyser.connect(audioContext.destination);
    
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    const updateLipSync = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      
      const lipSyncData: LipSyncData[] = [];
      const sampleRate = audioContext.sampleRate;
      const nyquist = sampleRate / 2;
      const binWidth = nyquist / bufferLength;

      Object.entries(visemeMapping).forEach(([viseme, config]) => {
        const startBin = Math.floor(config.freq[0] / binWidth);
        const endBin = Math.floor(config.freq[1] / binWidth);
        
        let sum = 0;
        let count = 0;
        
        for (let i = startBin; i <= endBin && i < bufferLength; i++) {
          sum += dataArrayRef.current![i];
          count++;
        }
        
        const average = count > 0 ? sum / count : 0;
        const normalizedIntensity = (average / 255) * config.intensity;
        
        if (normalizedIntensity > 0.05) { // Umbral más bajo para detectar
          lipSyncData.push({
            viseme,
            intensity: normalizedIntensity
          });
        }
      });

      // Si no hay datos significativos, usar silencio
      if (lipSyncData.length === 0) {
        lipSyncData.push({ viseme: 'viseme_sil', intensity: 0 });
      }

      applyLipSync(model, lipSyncData);
      animationFrameRef.current = requestAnimationFrame(updateLipSync);
    };

    updateLipSync();
  }, [applyLipSync]);

  const resetMouth = useCallback((model: THREE.Group) => {
    if (!model) return;

    model.traverse((child: THREE.Object3D) => {
      if (
        child instanceof THREE.SkinnedMesh &&
        child.morphTargetDictionary &&
        child.morphTargetInfluences
      ) {
        // Reset all visemes to closed mouth
        Object.keys(visemeMapping).forEach((viseme) => {
          const index = child.morphTargetDictionary![viseme];
          if (index !== undefined && child.morphTargetInfluences) {
            child.morphTargetInfluences[index] = 0;
          }
        });

        // Ensure mouth is closed
        const mouthOpenIndex = child.morphTargetDictionary!['mouthOpen'];
        if (mouthOpenIndex !== undefined && child.morphTargetInfluences) {
          child.morphTargetInfluences[mouthOpenIndex] = 0;
        }
      }
    });
  }, []);

  const stopLipSync = useCallback((model?: THREE.Group) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Reset mouth to closed position if model is provided
    if (model) {
      resetMouth(model);
    }
  }, [resetMouth]);

  return {
    analyzeAudio,
    stopLipSync,
    applyLipSync,
    resetMouth
  };
};
